import { Location, LocationStrategy, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CACHE_VERSION } from '@core/utils/version';
import {
  ApplicationsApi,
  CoreModule,
  getDateAdapterLocale,
  INIT_ENVIRONMENT,
  initializeAngularLocale,
  initializer,
  KEEPS_DATE_FORMATS,
  KeepsLocation,
  KeepsPathLocationStrategy,
  KP_I18N_CONFIG,
  KpMonitoringModule,
  provideIcons,
  TranslocoRootModule,
  UserProfileService,
  WorkspaceService,
} from '@keeps-platform-frontend-workspace/kp-keeps';
import {
  AbstractNavigationService,
  defaultConfig,
  FuseConfigModule,
  FuseLayoutModule,
  FuseSplashScreenModule,
  KEEPS_NAVIGATION_ITEMS,
} from '@keeps-platform-frontend-workspace/layout';
import { AppComponent } from 'app/app.component';
import { AuthModule } from 'app/shared/auth/auth.module';
import { ServicesModule } from 'app/shared/services/services.module';
import { AppStoreModule } from 'app/shared/store/store.module';

import { DateFnsAdapter, DateFnsModule } from '@angular/material-date-fns-adapter';
import { TranslocoService } from '@jsverse/transloco';
import { KpChatbotComponent } from '@keeps-platform-frontend-workspace/ui/kp-chatbot';
import { KpMenuComponent } from '@keeps-platform-frontend-workspace/ui/kp-menu';
import { UserMenuComponent } from '@keeps-platform-frontend-workspace/ui/kp-user-menu';
import { provideUILibTranslocoScope } from '@keeps-platform-frontend-workspace/ui/transloco-scope';
import { NAVIGATION_ITEMS, NavigationService } from 'app/shared/navigation';
import { environment } from 'environments/environment';
import { KeycloakAngularModule, KeycloakService, provideKeycloak } from 'keycloak-angular';
import { AppRoutingModule } from './app.routing';
import { WorkspaceModule } from './main/workspace/workspace.module';
import { KeepsMatPaginator } from './shared/services/keeps-mat-paginator.service';
import { SlashInterceptor } from './shared/store/slash.interceptor';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ActivityLogEffects, activityLogFeature } from './main/activity-log/store';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule.forRoot(environment.coreModuleConfig),
    KeycloakAngularModule,
    DateFnsModule,
    // Material
    MatButtonModule,
    MatIconModule,
    // Fuse migration
    FuseConfigModule.forRoot(defaultConfig),
    FuseSplashScreenModule,
    FuseLayoutModule,
    // App modules
    WorkspaceModule,
    AppRoutingModule,
    AppStoreModule,
    AuthModule,
    ServicesModule,
    HammerModule,
    TranslocoRootModule,
    KpChatbotComponent,
    KpMenuComponent,
    UserMenuComponent,
    KpMonitoringModule.forRoot({ appName: 'my-account-web', prod: environment.production }),
    StoreModule.forFeature(activityLogFeature),
    EffectsModule.forFeature(ActivityLogEffects),
  ],
  providers: [
    { provide: KP_I18N_CONFIG, useValue: { prodMode: environment.production, cacheVersion: CACHE_VERSION } },
    { provide: HTTP_INTERCEPTORS, useClass: SlashInterceptor, multi: true },
    provideKeycloak({
      config: environment.keycloakConfig,
    }),
    provideAppInitializer(() => {
      const initializerFn = initializer(
        inject(KeycloakService),
        inject(ApplicationsApi),
        inject(WorkspaceService),
        inject(UserProfileService),
        inject(TranslocoService),
        inject(KeepsPathLocationStrategy),
        inject(INIT_ENVIRONMENT),
      );
      return initializerFn();
    }),
    { provide: INIT_ENVIRONMENT, useValue: environment },
    {
      provide: LOCALE_ID,
      useFactory: initializeAngularLocale,
      deps: [KeycloakService],
    },
    { provide: MAT_DATE_LOCALE, useFactory: getDateAdapterLocale, deps: [KeycloakService] },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    { provide: MAT_DATE_FORMATS, useValue: KEEPS_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: KeepsMatPaginator },
    { provide: Location, useClass: KeepsLocation },
    { provide: KEEPS_NAVIGATION_ITEMS, useValue: NAVIGATION_ITEMS },
    { provide: AbstractNavigationService, useClass: NavigationService },
    { provide: LocationStrategy, useClass: KeepsPathLocationStrategy, deps: [PlatformLocation, WorkspaceService] },
    provideHttpClient(withInterceptorsFromDi()),
    provideUILibTranslocoScope(),
    provideIcons(),
  ],
})
export class AppModule {}
